import React, { useState, useEffect } from 'react';
import { checkCounterparty } from '../../../API/Api';
import CustomTooltip from './CustomTooltip';
import { normalizeSpaces } from '../../shared/utils';

const CounterpartyManager = ({
  formAuthority,
  readOnly = false,
  initialCounterparties = [],
  initialEnableCounterparties = 'no',
  onCounterpartiesChange,
  token,
  isAddCustomer = false, // 新增 prop 来区分是否在 Add Customer 中使用
  currentUrl, // 新增 currentUrl 参数
}) => {
  const [counterparties, setCounterparties] = useState([]);
  const [currentCounterparty, setCurrentCounterparty] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [enableCounterparties, setEnableCounterparties] = useState(false);

  // 初始化数据 - 只在组件挂载时执行一次，或者当初始数据从空变为有值时
  useEffect(() => {
    console.log('initialCounterparties-------', initialCounterparties);
    // 如果当前状态为空且初始数据有值，则更新状态
    if (counterparties.length === 0 && initialCounterparties.length > 0) {
      setCounterparties(initialCounterparties);
    }
    if (!enableCounterparties && initialEnableCounterparties === 'yes') {
      setEnableCounterparties(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 只在挂载时执行

  // 处理异步数据加载 - 当初始数据从空变为有值时更新
  useEffect(() => {
    // 只有当初始数据有值且当前状态为空时才更新
    if (initialCounterparties.length > 0 && counterparties.length === 0) {
      setCounterparties(initialCounterparties);
    }
    if (initialEnableCounterparties === 'yes' && !enableCounterparties) {
      setEnableCounterparties(true);
    }
  }, [initialCounterparties, initialEnableCounterparties, counterparties.length, enableCounterparties]);

  const handleCounterpartyInputChange = e => {
    const value = e.target.value;
    let normalizedValue = value;

    // 防止第一个字符是空格
    if (normalizedValue.startsWith(' ')) {
      normalizedValue = normalizedValue.trimStart();
    }

    // 防止输入两个连续的空格
    normalizedValue = normalizedValue.replace(/\s{2,}/g, ' ');

    setCurrentCounterparty(normalizedValue);
  };

  const handleCounterpartyKeyPress = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // 回车时先进行trim处理
      const trimmedValue = currentCounterparty.trim();
      setCurrentCounterparty(trimmedValue);
      checkCounterpartyName();
    }
  };

  const handleCounterpartyBlur = () => {
    // 输入框失去焦点时进行trim处理
    const trimmedValue = currentCounterparty.trim();
    setCurrentCounterparty(trimmedValue);
  };

  const checkCounterpartyName = async () => {
    // 点击Check按钮时先进行trim处理
    const trimmedValue = currentCounterparty.trim();
    setCurrentCounterparty(trimmedValue);

    const normalizedName = normalizeSpaces(trimmedValue);
    if (!normalizedName || isChecking) return;

    setIsChecking(true);
    try {
      const response = await checkCounterparty(normalizedName, token);
      const newCounterparty = {
        name: normalizedName,
        isCheck: response.data.isCheck,
        id: response.data.id || null,
      };

      const updatedCounterparties = [...counterparties, newCounterparty];
      setCounterparties(updatedCounterparties);
      onCounterpartiesChange?.({
        counterparties: updatedCounterparties,
        enableCounterparties: enableCounterparties ? 'yes' : 'no',
      });
      setCurrentCounterparty('');
    } catch (error) {
      console.error('Error checking counterparty:', error);
      // 可以添加 toast 错误提示
    } finally {
      setIsChecking(false);
    }
  };

  const removeCounterparty = index => {
    if (readOnly) return;
    const updatedCounterparties = counterparties.filter((_, i) => i !== index);
    setCounterparties(updatedCounterparties);
    onCounterpartiesChange?.({
      counterparties: updatedCounterparties,
      enableCounterparties: enableCounterparties ? 'yes' : 'no',
    });
  };

  // 在 /form/kyc-aml 页面不显示
  if (currentUrl && currentUrl === '/form/kyc-aml') {
    return null;
  }

  // 只在 Law 版本显示
  if (formAuthority !== 'Law' && formAuthority !== 'law') {
    return null;
  }

  return (
    <div className='grid gap-4'>
      <div>
        <div className='flex items-center gap-4 mb-3'>
          <div className='flex items-center gap-2'>
            <p className='block font-medium text-gray-700 py-1 px-2'>Counterparty</p>
            <CustomTooltip title="Counterparty refers to the opposing party involved in the client's legal matter such as individuals, entities, or firms on the other side of a dispute, transaction, or settlement." />
          </div>
          <div className='flex items-center gap-2'>
            <input
              type='checkbox'
              id='enableCounterparties'
              checked={enableCounterparties}
              onChange={e => {
                const newValue = e.target.checked;
                setEnableCounterparties(newValue);
                onCounterpartiesChange?.({
                  counterparties,
                  enableCounterparties: newValue ? 'yes' : 'no',
                });
              }}
              disabled={readOnly}
              className='w-4 h-4'
            />
            <label htmlFor='enableCounterparties' className='text-sm font-medium text-gray-700'>
              Check Counterparty
            </label>
          </div>
        </div>

        {enableCounterparties && (
          <>
            <div className='flex items-center gap-2 mb-3'>
              <input
                maxLength={800}
                disabled={isChecking || readOnly}
                type='text'
                placeholder='Enter Counterparty Name'
                className='border border-gray-300 rounded-md p-2 flex-1'
                value={currentCounterparty}
                onChange={handleCounterpartyInputChange}
                onKeyPress={handleCounterpartyKeyPress}
                onBlur={handleCounterpartyBlur}
              />
              <button
                type='button'
                onClick={checkCounterpartyName}
                disabled={!currentCounterparty.trim() || isChecking || readOnly}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  !currentCounterparty.trim() || isChecking || readOnly
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {isChecking ? 'Checking...' : 'Check'}
              </button>
            </div>

            {/* Counterparty List */}
            {counterparties.length > 0 ? (
              <div className='space-y-1'>
                {counterparties.map((counterparty, index) => (
                  <div key={index} className='flex items-center justify-between bg-gray-50 p-2 rounded-md'>
                    <div className='flex items-center gap-2'>
                      <span className='text-sm'>{counterparty.name}</span>
                      {counterparty.isCheck ? (
                        isAddCustomer ? (
                          <span
                            className='text-red-500 cursor-help'
                            title='This counterparty already exists in the database.'
                            style={{ fontSize: '18px', fontWeight: 'bold', lineHeight: '1' }}
                          >
                            ✗
                          </span>
                        ) : (
                          <div className='flex items-center gap-1'>
                            <span
                              className='text-red-500'
                              style={{ fontSize: '18px', fontWeight: 'bold', lineHeight: '1' }}
                            >
                              ✗
                            </span>
                            <span className='text-xs text-red-600'>
                              This counterparty already exists in the database
                            </span>
                          </div>
                        )
                      ) : isAddCustomer ? (
                        <span
                          className='text-green-500'
                          style={{ fontSize: '18px', fontWeight: 'bold', lineHeight: '1' }}
                        >
                          ✓
                        </span>
                      ) : (
                        <div className='flex items-center gap-1'>
                          <span
                            className='text-green-500'
                            style={{ fontSize: '18px', fontWeight: 'bold', lineHeight: '1' }}
                          >
                            ✓
                          </span>
                          <span className='text-xs text-green-600'>
                            This counterparty does not exist in the database
                          </span>
                        </div>
                      )}
                    </div>
                    {!readOnly && (
                      <button
                        type='button'
                        onClick={() => removeCounterparty(index)}
                        className='text-red-500 hover:text-red-700 text-sm'
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className='text-sm text-gray-500 italic'>No counterparties found. Add one above to get started.</div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CounterpartyManager;
